import { useMemo, useRef, useState, useEffect } from 'react';
import { Check, ArrowRight, Play, Instagram } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import BookingCTA from '../components/BookingCTA';
import SEO from '../components/SEO';
import { useOfferte } from '../context/OfferteContext';
import { useLang } from '../context/LanguageContext';

const ProjectVideo = ({ src, alt, labels }) => {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const setMid = () => {
      if (!isNaN(v.duration) && v.duration > 0.2) {
        try { v.currentTime = Math.min(v.duration * 0.25, 1.5); } catch (_) {}
      }
      setReady(true);
    };
    if (v.readyState >= 1) setMid();
    else v.addEventListener('loadedmetadata', setMid, { once: true });
  }, [src]);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().then(() => setPlaying(true)).catch(() => {});
  };
  const pause = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    setPlaying(false);
  };

  return (
    <div
      className="relative w-full aspect-[9/16] overflow-hidden rounded-t-xl bg-black group/video"
      onMouseEnter={play}
      onMouseLeave={pause}
      onTouchStart={play}
      onTouchEnd={pause}
    >
      <video
        ref={videoRef}
        src={src}
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
        aria-label={alt}
      />

      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="absolute inset-0 bg-black/25" />
          <div className="relative w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl transition-transform group-hover/video:scale-110">
            <Play className="w-6 h-6 text-[#0a0a0b] ml-1" fill="#0a0a0b" />
          </div>
        </div>
      )}

      <div className="absolute top-3 left-3 px-3 py-1 rounded-md bg-black/60 backdrop-blur text-white text-[11px] tracking-wider uppercase flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${playing ? 'bg-red-500 animate-pulse' : 'bg-[#2f6bff]'}`} />
        {playing ? labels.live : labels.video}
      </div>

      {!ready && (
        <div className="absolute inset-0 bg-[#0a0a0b] animate-pulse" />
      )}
    </div>
  );
};

const BeforeAfter = ({ before, after, alt, labels }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const p = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPos(p);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const start = (e) => {
    dragging.current = true;
    setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const stop = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={start}
      onTouchMove={onMove}
      onTouchEnd={stop}
      className="relative w-full aspect-[16/10] overflow-hidden rounded-t-xl select-none cursor-ew-resize bg-black"
    >
      <img src={after} alt={`${alt} — ${labels.after}`} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img src={before} alt={`${alt} — ${labels.before}`} className="absolute inset-0 w-full h-full object-cover" style={{ width: `${100 / (pos / 100)}%`, maxWidth: 'none' }} />
      </div>
      <div className="absolute top-3 left-3 px-3 py-1 rounded-md bg-black/60 backdrop-blur text-white text-[11px] tracking-wider uppercase">{labels.before}</div>
      <div className="absolute top-3 right-3 px-3 py-1 rounded-md bg-[#2f6bff]/90 text-white text-[11px] tracking-wider uppercase">{labels.after}</div>
      <div className="absolute inset-y-0 pointer-events-none" style={{ left: `calc(${pos}% - 1px)` }}>
        <div className="w-0.5 h-full bg-white/90 shadow-[0_0_12px_rgba(255,255,255,0.4)]" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-lg">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 6 9 12 15 18" />
            <polyline points="9 6 15 12 9 18" transform="translate(12 0)" />
          </svg>
        </div>
      </div>
    </div>
  );
};

const Portfolio = () => {
  const [active, setActive] = useState('all');
  const { openOfferte } = useOfferte();
  const { c, ui } = useLang();
  const t = ui.portfolio;
  const labels = { before: t.before, after: t.after, video: t.video, live: t.live };

  const availableCategories = useMemo(() => {
    const withContent = new Set(c.portfolioProjects.map(p => p.category));
    return c.portfolioCategories.filter(cat => cat.id === 'all' || withContent.has(cat.id));
  }, [c]);

  const projects = useMemo(() => {
    if (active === 'all') return c.portfolioProjects;
    return c.portfolioProjects.filter(p => p.category === active);
  }, [active, c]);

  return (
    <div className="bg-[#0a0a0b] text-white pt-32 pb-24">
      <SEO
        path="/portfolio"
        title="Projecten — Exclusieve auto's, oldtimers & campers"
        description="Bekijk onze detailingprojecten: BMW i5 M60, Porsche Macan S, Range Rover Sport en klassieke oldtimers. Video's en resultaten."
        keywords="detailing portfolio, autopoetsen projecten, keramische coating resultaat, oldtimer detailing, supercar detailing"
      />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <SectionLabel>{t.label}</SectionLabel>
        <h1 className="mt-5 text-5xl md:text-7xl font-medium tracking-tight leading-[1.02] max-w-4xl">
          {t.title}
        </h1>
        <p className="mt-6 text-white/60 max-w-2xl text-lg leading-relaxed">
          {t.subtitle}
        </p>

        <div className="mt-12 flex flex-wrap gap-2">
          {availableCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActive(cat.id)}
              className={`px-5 py-2.5 rounded-full text-sm border transition-colors ${
                active === cat.id
                  ? 'bg-[#2f6bff] border-[#2f6bff] text-white'
                  : 'border-white/15 text-white/70 hover:text-white hover:border-white/30'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {projects.map(p => (
            <article key={p.id} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden flex flex-col group">
              <div className="relative">
                {p.video ? (
                  <ProjectVideo src={p.video} alt={p.title} labels={labels} />
                ) : (
                  <BeforeAfter before={p.before} after={p.after} alt={p.title} labels={labels} />
                )}
                <span className="absolute bottom-3 right-3 px-3 py-1.5 rounded-md bg-[#2f6bff] text-white text-[11px] tracking-wider uppercase font-medium">
                  {p.badge}
                </span>
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h3 className="text-2xl md:text-3xl font-medium tracking-tight">{p.title}</h3>
                <p className="mt-2 text-white/55 text-sm italic">{p.subtitle}</p>
                <ul className="mt-5 grid grid-cols-2 gap-y-2 gap-x-4">
                  {p.treatments.map(tr => (
                    <li key={tr} className="flex items-start gap-2 text-white/75 text-sm">
                      <span className="mt-1 w-4 h-4 rounded-full bg-[#2f6bff]/20 text-[#2f6bff] flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3" />
                      </span>
                      <span>{tr}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => openOfferte({
                    treatment: p.title,
                    notes: t.notes(p.title),
                  })}
                  data-testid={`portfolio-offerte-${p.id}`}
                  className="mt-6 inline-flex items-center gap-2 self-start px-5 py-3 rounded-md bg-white/5 hover:bg-[#2f6bff] hover:text-white text-white/85 text-sm font-medium transition-colors border border-white/10 hover:border-[#2f6bff] group"
                >
                  {t.requestResult}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </article>
          ))}
        </div>

        {projects.length === 0 && (
          <div className="mt-16 text-center py-16 text-white/50">
            {t.empty}
          </div>
        )}

        {/* Instagram CTA */}
        <div className="mt-16 flex justify-center">
          <a
            href="https://www.instagram.com/shiny.cleaning.nl/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="portfolio-instagram-cta"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-md bg-[#2f6bff] hover:bg-[#1f56e8] text-white text-sm font-medium transition-colors"
          >
            <Instagram className="w-4 h-4" />
            {t.instagram}
          </a>
        </div>

        <div className="mt-24 p-10 md:p-16 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent text-center">
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">
            {t.ctaTitle}
          </h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">
            {t.ctaText}
          </p>
          <div className="mt-8 flex justify-center">
            <BookingCTA size="lg" align="center" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
